import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBag, Trash2, ArrowLeft, Check, Sparkles, Calendar, MapPin, Music, MessageCircle, Image as ImageIcon } from 'lucide-react';
import { getCart, removeFromCart, clearCart, pricingTiers, extraOptions } from '../mock';
import PayPalCheckout from '../components/PayPalCheckout';
import { useTranslation } from '../LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { t, price, currency } = useTranslation();
  const [items, setItems] = useState([]);
  const [invitations, setInvitations] = useState({}); // id -> full data
  const [paid, setPaid] = useState(false);
  const [publishedLinks, setPublishedLinks] = useState([]);

  useEffect(() => {
    const cart = getCart();
    setItems(cart);
    cart.forEach(async (item) => {
      try {
        const res = await axios.get(`${API}/invitations/${item.invitationId}`);
        setInvitations((m) => ({ ...m, [item.invitationId]: res.data }));
      } catch (e) {}
    });
  }, []);

  const remove = (id) => {
    removeFromCart(id);
    setItems(getCart());
  };

  const extraLabel = (id) => t(`${id}.label`);

  // Compute totals - each cart item has its own tier + extras
  const itemsWithPricing = items.map((it) => {
    const tier = pricingTiers.find((x) => x.id === it.tier) || pricingTiers[0];
    const extrasCount = (it.extras || []).length;
    const itemTotal = tier.price + extrasCount * 0.5;
    return { ...it, tier, extrasCount, itemTotal };
  });
  const total = itemsWithPricing.reduce((s, x) => s + x.itemTotal, 0);

  const handlePaymentSuccess = () => {
    const links = items.map((it) => `${window.location.origin}/invito/${it.invitationId}`);
    setPublishedLinks(links);
    setPaid(true);
    clearCart();
  };

  if (items.length === 0 && !paid) {
    return (
      <section className="max-w-2xl mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-[color:var(--burgundy)]/10 mx-auto flex items-center justify-center mb-6">
          <ShoppingBag size={32} className="text-[color:var(--burgundy)]" />
        </div>
        <h1 className="font-serif text-4xl mb-4">{t('cart.empty.title1')} <span className="italic-serif">{t('cart.empty.title2')}</span>.</h1>
        <p className="text-[color:var(--ink-soft)] mb-8">{t('cart.empty.subtitle')}</p>
        <Link to="/crea" className="btn-primary inline-flex items-center gap-2"><Sparkles size={16} /> {t('cart.empty.cta')}</Link>
      </section>
    );
  }

  if (paid) {
    return (
      <section className="max-w-2xl mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-6">
          <Check size={40} className="text-green-600" />
        </div>
        <h1 className="font-serif text-4xl mb-4">{t('cart.paid.title1')} <span className="italic-serif">{t('cart.paid.title2')}</span>!</h1>
        <p className="text-[color:var(--ink-soft)] mb-8">{t('cart.paid.subtitle')}</p>
        <div className="space-y-3">
          {publishedLinks.map((link, i) => (
            <div key={i} className="bg-white/70 border border-[color:var(--border)] rounded-xl p-4 flex flex-col sm:flex-row items-center gap-3">
              <input readOnly value={link} onClick={(e) => e.target.select()} className="flex-1 px-3 py-2 bg-[color:var(--cream)] rounded-lg text-sm text-[color:var(--ink)] w-full" />
              <div className="flex gap-2">
                <button onClick={() => navigator.clipboard.writeText(link)} className="px-4 py-2 rounded-lg bg-[color:var(--burgundy)] text-[#f8ede0] text-sm">{t('cart.paid.copy')}</button>
                <a href={link} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg border border-[color:var(--border)] text-sm">{t('cart.paid.open')}</a>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/crea" className="text-sm text-[color:var(--burgundy)] hover:underline">{t('cart.paid.another')}</Link>
        </div>
      </section>
    );
  }

  // Static preview card (non-animated) using invitation data
  const StaticPreview = ({ inv }) => {
    if (!inv) return null;
    const p = inv.palette;
    const fontFamily = inv.font_style === 'serif' ? "'Cormorant Garamond', serif" : "'Inter', sans-serif";
    const bg = inv.custom_photo_url || inv.template_image;
    const dateStr = inv.date ? new Date(inv.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    return (
      <div className="phone-frame phone-frame-lg mx-auto">
        <div className="phone-screen overflow-y-auto" style={{ background: p.bg }}>
          <div className="relative min-h-full" style={{ color: p.text, fontFamily }}>
            {bg && <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
            <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 py-6" style={{ minHeight: '100%' }}>
              <div className="text-[9px] tracking-[0.3em] uppercase mb-2" style={{ color: p.accent }}>{inv.event_name}</div>
              <div className="text-4xl leading-none mb-1">{inv.celebrant}</div>
              {inv.age && <div className="text-xs italic opacity-80 mb-2">{inv.age}</div>}
              <div className="w-8 h-px my-3" style={{ background: p.accent }}></div>
              <div className="text-xs italic">{dateStr}</div>
              <div className="text-xs italic mb-2">ore {inv.time}</div>
              <div className="text-[10px] opacity-80 mb-3">{inv.location}</div>
              {inv.program && <div className="text-[9px] opacity-70 leading-relaxed px-2 mb-2">{inv.program}</div>}
              {inv.message && <div className="text-[10px] italic opacity-90 px-2 mb-3">“{inv.message}”</div>}
              {inv.whatsapp_number && (
                <div className="mt-2 px-3 py-1 rounded-full text-[9px] inline-flex items-center gap-1" style={{ background: p.accent, color: p.bg }}>✓ WhatsApp RSVP</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="mb-10">
        <Link to="/crea" className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-soft)] hover:text-[color:var(--burgundy)] mb-3">
          <ArrowLeft size={14} /> {t('cart.back')}
        </Link>
        <div className="eyebrow mb-3">{t('cart.eyebrow')}</div>
        <h1 className="font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05]">
          {t('cart.title1')} <span className="italic-serif">{t('cart.title2')}</span>.
        </h1>
      </div>

      <div className="grid lg:grid-cols-5 gap-10 items-start">
        {/* LEFT: static preview + item details */}
        <div className="lg:col-span-3 space-y-6">
          {itemsWithPricing.map((it) => {
            const inv = invitations[it.invitationId];
            return (
              <div key={it.invitationId} className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="eyebrow mb-1">{it.templateName}</div>
                    <h3 className="font-serif text-2xl text-[color:var(--ink)]">{it.celebrant}</h3>
                    <div className="text-sm text-[color:var(--ink-soft)] italic">{it.event}</div>
                    <div className="mt-2 inline-block px-3 py-1 rounded-full text-xs bg-[color:var(--burgundy)]/10 text-[color:var(--burgundy)] font-medium">
                      {t('builder.summary.plan')} {it.tier.name}
                    </div>
                  </div>
                  <button onClick={() => remove(it.invitationId)} className="text-[color:var(--ink-soft)] hover:text-red-600 p-1">
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Static preview */}
                <div className="mb-4">
                  <div className="eyebrow mb-2">{t('cart.preview.title')}</div>
                  <StaticPreview inv={inv} />
                </div>

                {/* Details grid */}
                {inv && (
                  <div className="grid grid-cols-2 gap-3 text-xs text-[color:var(--ink-soft)] mb-4">
                    <div className="flex items-center gap-2"><Calendar size={12} className="text-[color:var(--burgundy)]" /> {new Date(inv.date).toLocaleDateString('it-IT')} · {inv.time}</div>
                    <div className="flex items-center gap-2"><MapPin size={12} className="text-[color:var(--burgundy)]" /> {inv.location}</div>
                    {inv.music && inv.music !== 'nessuna' && (
                      <div className="flex items-center gap-2"><Music size={12} className="text-[color:var(--burgundy)]" /> {inv.music === '__custom' ? 'Musica personalizzata' : inv.music}</div>
                    )}
                    {inv.whatsapp_number && (
                      <div className="flex items-center gap-2"><MessageCircle size={12} className="text-[color:var(--burgundy)]" /> RSVP WhatsApp</div>
                    )}
                    {inv.custom_photo_url && (
                      <div className="flex items-center gap-2"><ImageIcon size={12} className="text-[color:var(--burgundy)]" /> Foto personalizzata</div>
                    )}
                  </div>
                )}

                {/* Extras list */}
                {it.extras && it.extras.length > 0 && (
                  <div>
                    <div className="eyebrow mb-2">Extra selezionati</div>
                    <div className="flex flex-wrap gap-1.5">
                      {it.extras.map((ex) => (
                        <span key={ex} className="text-[10px] px-2 py-1 rounded-full bg-[color:var(--burgundy)]/8 text-[color:var(--burgundy)]">
                          {extraLabel(ex)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT: brief summary + payment */}
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <div className="bg-[color:var(--burgundy)] rounded-2xl p-6 text-[#f8ede0]">
            <h3 className="font-serif text-2xl mb-5">{t('cart.summary')}</h3>

            <div className="space-y-3 text-sm mb-5 pb-5 border-b border-white/20">
              {itemsWithPricing.map((it, idx) => (
                <div key={it.invitationId + idx}>
                  <div className="flex justify-between font-medium">
                    <span>{t('builder.summary.plan')} {it.tier.name}</span>
                    <span>{price(it.tier.price)}</span>
                  </div>
                  <div className="text-xs opacity-70 -mt-0.5">{it.celebrant}</div>
                  {(it.extras || []).map((ex) => (
                    <div key={ex} className="flex justify-between text-xs opacity-85 mt-1 ml-2">
                      <span>+ {extraLabel(ex)}</span>
                      <span>{price(0.5)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex items-baseline justify-between mb-6">
              <span className="text-sm opacity-80">{t('cart.total')}</span>
              <span className="font-serif text-4xl">{price(total)}</span>
            </div>

            <div className="bg-[#f8ede0] rounded-xl p-4">
              <div className="text-xs text-[color:var(--ink-soft)] mb-3 text-center">{t('cart.payHint')}</div>
              <PayPalCheckout
                amount={total}
                currency={currency}
                description={`Inviti (${items.length}) - ${itemsWithPricing[0]?.celebrant}`.slice(0, 120)}
                invitationId={items[0]?.invitationId}
                tier={items[0]?.tier}
                extras={items.flatMap((i) => i.extras || [])}
                onSuccess={handlePaymentSuccess}
              />
            </div>

            <div className="text-[10px] opacity-70 text-center mt-4 tracking-widest">
              {t('cart.secure')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
